// src/pages/SellerIntelligence/ProfitMargin/ProfitDashboard.js
// Page 1 — what Amazon charged, what the seller kept, and which products lose money.
//
// Reading surface only: costs are entered on Cost Setup. Every figure here comes
// from the metric library via /seller-intelligence/profit; the page performs no
// arithmetic of its own beyond formatting.
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiAlertTriangle, FiChevronDown, FiChevronRight, FiInfo,
  FiRefreshCw, FiSettings,
} from 'react-icons/fi';

import styles from '../SellerIntelligence.module.css';
import local from './ProfitMargin.module.css';
import AuthContext from '../../../context/AuthContext';
import {
  Money, Percent, FindingBadges, MaturityBadge, Blank, EstBadge,
  count, formatDate, formatDateTime,
} from './format';
import Findings from './components/Findings';
import ProfitChange from './components/ProfitChange';
import ProfitTrend from './components/ProfitTrend';
import {
  getDashboard, getSkus, getPriceBands, getOrderItems,
} from './services/profitApi';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'loss', label: 'Loss-making' },
  { id: 'no_cost', label: 'Cost not set' },
  { id: 'below_floor', label: 'Below margin floor' },
  { id: 'below_breakeven', label: 'Below breakeven' },
  { id: 'returns', label: 'Has returns' },
  { id: 'fba', label: 'FBA only' },
  { id: 'mfn', label: 'Seller-fulfilled only' },
];

const ProfitDashboard = () => {
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;

  // Left empty on first load so the backend can choose the latest settled
  // period. Month-to-date would be empty for the first half of every month,
  // because settlements only post fortnightly.
  const [range, setRange] = useState({ start: '', end: '' });
  const [applied, setApplied] = useState({});
  const [dashboard, setDashboard] = useState(null);
  const [skus, setSkus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('products');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [bands, setBands] = useState({});
  const [orderItems, setOrderItems] = useState(null);
  const [page, setPage] = useState(1);
  const tableRef = useRef(null);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const [summary, table] = await Promise.all([
        getDashboard(token, applied),
        getSkus(token, applied),
      ]);
      setDashboard(summary);
      setSkus(table);
      // Adopt the window the server resolved, so the pickers show the period
      // actually on screen rather than staying blank.
      if (summary?.window?.start) {
        setRange((current) => (current.start ? current
          : { start: summary.window.start, end: summary.window.end }));
      }
    } catch (err) {
      setError(err);
      setDashboard(null);
      setSkus(null);
    } finally {
      setLoading(false);
    }
  }, [token, applied]);

  useEffect(() => { load(); }, [load]);

  // The order-item view is the audit trail and is paginated, so it loads only
  // when opened rather than alongside every dashboard refresh.
  useEffect(() => {
    if (view !== 'orders' || !token) return;
    let cancelled = false;
    setOrdersLoading(true);
    setOrdersError(null);
    getOrderItems(token, applied, page)
      .then((data) => { if (!cancelled) setOrderItems(data); })
      // Reported inside the panel, not only in the page-level banner: the banner
      // sits far above the table and a blank grid otherwise looks like no data.
      .catch((err) => { if (!cancelled) { setOrderItems(null); setOrdersError(err); } })
      .finally(() => { if (!cancelled) setOrdersLoading(false); });
    return () => { cancelled = true; };
  }, [view, token, applied, page]);

  const toggleRow = async (sku) => {
    if (expanded === sku) { setExpanded(null); return; }
    setExpanded(sku);
    if (bands[sku]) return;
    try {
      const data = await getPriceBands(token, sku, applied);
      setBands((current) => ({ ...current, [sku]: data.bands }));
    } catch {
      setBands((current) => ({ ...current, [sku]: [] }));
    }
  };

  const floor = skus?.min_acceptable_margin_pct ?? 0;

  const rows = useMemo(() => {
    const all = skus?.rows ?? [];
    const term = search.trim().toLowerCase();
    return all.filter((row) => {
      if (term && !row.sku.toLowerCase().includes(term)) return false;
      if (filter === 'loss') return row.has_cogs && row.net_profit < 0;
      if (filter === 'no_cost') return !row.has_cogs && row.units > 0;
      if (filter === 'below_floor') {
        return row.has_cogs && row.margin_pct !== null && row.margin_pct < floor;
      }
      if (filter === 'below_breakeven') return row.below_breakeven;
      if (filter === 'returns') return row.refunded_units > 0;
      // MIXED counts for both channels — the SKU genuinely trades on each.
      if (filter === 'fba') return row.channel === 'AFN' || row.channel === 'MIXED';
      if (filter === 'mfn') return row.channel === 'MFN' || row.channel === 'MIXED';
      return true;
    });
  }, [skus, search, filter, floor]);

  // Findings follow the table's search and filter, so the two lists always
  // describe the same products. Unfiltered, every finding shows.
  const visibleSkus = useMemo(() => (filter === 'all' && !search.trim()
    ? null : new Set(rows.map((row) => row.sku))), [rows, filter, search]);

  const showSku = (sku) => {
    setView('products');
    setFilter('all');
    setSearch(sku);
    setExpanded(sku);
    requestAnimationFrame(() => tableRef.current?.scrollIntoView({
      behavior: 'smooth', block: 'start',
    }));
  };

  const header = (
    <div className={styles.header}>
      <button className={styles.backButton} onClick={() => navigate('/dashboard/seller-intelligence')}>
        <FiArrowLeft /> Back
      </button>
      <div className={styles.titleSection}>
        <div className={styles.iconTitle}><h1>Profit &amp; Margin</h1></div>
        <p>What Amazon charged, what you kept, and which products are losing money</p>
      </div>
    </div>
  );

  if (error?.notConnected) {
    return (
      <div className={styles.featureContainer}>{header}
        <div className={styles.content}>
          <div className={`${local.banner} ${local.bannerWarn}`}>
            <FiAlertTriangle className={local.bannerIcon} />
            <div>{error.message}</div>
          </div>
        </div>
      </div>
    );
  }

  const coverage = dashboard?.coverage;
  const coverageLow = (coverage?.revenue_pct ?? 0) < 80;

  return (
    <div className={styles.featureContainer}>
      {header}
      <div className={styles.content}>

        <div className={local.bar}>
          <label className={local.field}>
            <span>From</span>
            <input type="date" value={range.start}
              onChange={(e) => setRange({ ...range, start: e.target.value })} />
          </label>
          <label className={local.field}>
            <span>To</span>
            <input type="date" value={range.end}
              onChange={(e) => setRange({ ...range, end: e.target.value })} />
          </label>
          <button className={local.primary} onClick={() => setApplied(range)} disabled={loading}>
            <FiRefreshCw /> {loading ? 'Loading…' : 'Apply'}
          </button>
          <div className={local.spacer} />
          <span className={local.muted}>
            Last synced {formatDateTime(dashboard?.last_synced)}
            {dashboard?.data_coverage?.start && (
              <> · transactions cover {formatDate(dashboard.data_coverage.start)} –{' '}
                {formatDate(dashboard.data_coverage.end)}
                {dashboard.data_coverage.source === 'FINANCES_API'
                  ? ' (Amazon transaction history)' : ' (settlement reports)'}</>
            )}
          </span>
          <button className={coverage?.skus_with_cost === 0 ? local.primary : local.ghost}
            onClick={() => navigate('/dashboard/seller-intelligence/cost-setup')}>
            <FiSettings /> {coverage?.skus_with_cost === 0 ? 'Upload COGS' : 'Cost setup'}
          </button>
        </div>

        {error && !error.notConnected && (
          <div className={`${local.banner} ${local.bannerError}`}>
            <FiAlertTriangle className={local.bannerIcon} />
            <div>
              <strong>{error.reportUnavailable ? 'Data not ready' : 'Could not load'}</strong>
              <div>{error.message}</div>
            </div>
          </div>
        )}

        {/* A window wider than the data shows the same totals as a narrower
            one, which otherwise reads as a broken date filter. */}
        {dashboard?.data_coverage?.window_exceeds_data && (
          <div className={`${local.banner} ${local.bannerWarn}`}>
            <FiAlertTriangle className={local.bannerIcon} />
            <div>
              The period you chose runs beyond the transactions on file, which cover{' '}
              {formatDate(dashboard.data_coverage.start)} to{' '}
              {formatDate(dashboard.data_coverage.end)}. Figures cover only those dates.
              {dashboard.data_coverage.source !== 'FINANCES_API'
                && ' Use "Fetch fees data for a year" to pull a full year from Amazon.'}
            </div>
          </div>
        )}

        {dashboard?.advertising && !dashboard.advertising.connected && (
          <div className={`${local.banner} ${local.bannerInfo}`}>
            <FiInfo className={local.bannerIcon} />
            <div>{dashboard.advertising.message} Figures here are profit before advertising.</div>
          </div>
        )}

        {dashboard?.notes?.map((note) => (
          <div key={note} className={`${local.banner} ${local.bannerWarn}`}>
            <FiAlertTriangle className={local.bannerIcon} />
            <div>{note}</div>
          </div>
        ))}

        {dashboard && (
          <>
            <div className={local.tiles}>
              {[
                ['today', 'Today'],
                ['yesterday', 'Yesterday'],
                ['month_to_date', 'Month to date'],
                ['last_month', 'Last month'],
                ['selected', 'Selected period'],
              ].map(([key, label]) => {
                const tile = dashboard.tiles?.[key];
                if (!tile) return null;
                return (
                  <div key={key} className={local.tile}>
                    <div className={local.tileLabel}>
                      {label}{' '}
                      {!tile.has_activity
                        ? <span className={`${local.badge} ${local.badgeNeutral}`}>
                            no activity
                          </span>
                        : tile.source === 'ORDERS'
                          ? <span className={`${local.badge} ${local.badgeInfo}`}>
                              not settled yet
                            </span>
                          : <MaturityBadge maturity={tile.maturity} />}
                    </div>
                    <div className={local.tileHeroLabel}>
                      Net profit <EstBadge show={tile.estimated_revenue > 0} />
                    </div>
                    <div className={local.tileHero}>
                      {tile.has_activity ? <Money value={tile.net_profit} signed /> : <Blank />}
                    </div>
                    <dl style={{ margin: 0 }}>
                      <div className={local.tileRow}>
                        <dt>Net sales</dt><dd><Money value={tile.net_sales} /></dd>
                      </div>
                      <div className={local.tileRow}>
                        <dt>Margin</dt><dd><Percent value={tile.margin_pct} signed /></dd>
                      </div>
                      <div className={local.tileRow}>
                        <dt>Units</dt><dd>{count(tile.units)}</dd>
                      </div>
                    </dl>
                    {tile.source === 'ORDERS' ? (
                      <p className={local.note}>
                        Sales as placed, from {tile.orders} orders — Amazon has not
                        settled this period, so its fees are not known and profit
                        cannot be calculated yet.
                      </p>
                    ) : !tile.has_activity ? (
                      <p className={local.note}>
                        Nothing settled in this period yet. Amazon settles fortnightly,
                        so the current month is usually empty until its first payout.
                      </p>
                    ) : tile.excluded_revenue > 0 && (
                      <p className={local.note}>
                        <Money value={tile.excluded_revenue} /> of sales from SKUs with
                        no cost is left out of these figures.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            <div className={local.card}>
              <div className={local.cardHead}>
                <h2 className={local.cardTitle}>Coverage</h2>
                <span className={local.muted}>
                  {coverage?.skus_with_cost ?? 0} of {coverage?.skus_total ?? 0} SKUs have a cost
                </span>
              </div>
              <div className={`${local.coverageBar} ${coverageLow ? local.coverageLow : ''}`}>
                <div className={local.coverageFill}
                  style={{ width: `${Math.min(coverage?.revenue_pct ?? 0, 100)}%` }} />
              </div>
              {coverage?.skus_with_cost === 0 ? (
                <p className={local.note}>
                  <strong>No costs entered yet.</strong> Profit and margin cannot be
                  calculated for any of the {coverage?.skus_total ?? 0} SKUs that traded
                  in this period, covering <Money value={coverage?.total_revenue} /> of
                  revenue. The figures above are revenue less Amazon's fees only.{' '}
                  <button className={local.linkBtn}
                    onClick={() => navigate('/dashboard/seller-intelligence/cost-setup')}>
                    Enter costs
                  </button>
                </p>
              ) : (
                <p className={local.note}>
                  <strong><Percent value={coverage?.revenue_pct} /></strong> of revenue is
                  measurable — <Money value={coverage?.covered_revenue} /> of{' '}
                  <Money value={coverage?.total_revenue} />.
                  {coverageLow && ' Enter costs on Cost Setup to make the rest measurable.'}
                  {coverage?.estimated_revenue > 0 && (
                    <> <Money value={coverage.estimated_revenue} /> of it rests on your
                      percentage-of-price estimate and is marked <EstBadge show />.</>
                  )}
                </p>
              )}
              {/* Data gaps block a calculation rather than cost money, so they sit
                  with the coverage statement and are never ranked. */}
              {skus?.data_gaps?.length > 0 && (
                <ul className={local.note} style={{ paddingLeft: 18, margin: '6px 0 0' }}>
                  {skus.data_gaps.slice(0, 5).map((gap) => (
                    <li key={`${gap.code}-${gap.sku}`}>{gap.headline}. {gap.action}</li>
                  ))}
                  {skus.data_gaps.length > 5 && (
                    <li>and {skus.data_gaps.length - 5} more like these.</li>
                  )}
                </ul>
              )}
            </div>

            <div className={local.card}>
              <div className={local.cardHead}>
                <h2 className={local.cardTitle}>What Amazon charged</h2>
                <span className={local.muted}>
                  Fee load <strong><Percent value={dashboard.fees?.fee_load_pct} /></strong> of{' '}
                  <Money value={dashboard.fees?.gross_revenue} /> gross
                </span>
              </div>
              <div className={local.tableScroll}>
                <table className={local.table}>
                  <thead>
                    <tr>
                      <th>Fee type</th>
                      <th className={local.numeric}>Amount</th>
                      <th className={local.numeric}>% of revenue</th>
                      <th className={local.numeric}>Per unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(dashboard.fees?.lines ?? []).map((line) => (
                      <tr key={line.charge_type}>
                        <td>{line.charge_type.replaceAll('_', ' ').toLowerCase()}</td>
                        <td className={local.numeric}><Money value={line.amount} /></td>
                        <td className={local.numeric}><Percent value={line.share_of_revenue_pct} /></td>
                        <td className={local.numeric}><Money value={line.per_unit} paise /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {dashboard.reconcile?.rows?.length > 0 && (
                <p className={local.note}>
                  {dashboard.reconcile.rows[0].reconciled
                    ? <>Settlement {dashboard.reconcile.rows[0].settlement_id} reconciles
                      exactly against its {count(dashboard.reconcile.rows[0].line_count)} line items.</>
                    : <>Settlement {dashboard.reconcile.rows[0].settlement_id} differs from its
                      line items by <Money value={dashboard.reconcile.rows[0].difference} signed />.</>}
                </p>
              )}
            </div>
          </>
        )}

        <Findings token={token} payload={skus} visibleSkus={visibleSkus}
          onShowSku={showSku} onChanged={load} />

        <div className={local.card} ref={tableRef}>
          <div className={local.cardHead}>
            <div className={local.toggle}>
              <button className={`${local.toggleBtn} ${view === 'products' ? local.toggleBtnOn : ''}`}
                onClick={() => setView('products')}>Products</button>
              <button className={`${local.toggleBtn} ${view === 'orders' ? local.toggleBtnOn : ''}`}
                onClick={() => setView('orders')}>Order items</button>
            </div>
            {view === 'products' && (
              <div className={local.bar} style={{ margin: 0 }}>
                <input className={local.costInput} style={{ width: 170, textAlign: 'left' }}
                  placeholder="Search SKU" value={search}
                  onChange={(e) => setSearch(e.target.value)} />
                <select value={filter} onChange={(e) => setFilter(e.target.value)}
                  style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #e3e6ea' }}>
                  {FILTERS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
              </div>
            )}
          </div>

          {view === 'products' ? (
            loading ? <div className={local.empty}>Loading products…</div>
              : rows.length === 0 ? (
                <div className={local.empty}>
                  {(skus?.rows?.length ?? 0) === 0
                    ? <>No settled trading in {dashboard?.window?.start} to{' '}
                        {dashboard?.window?.end}. Settlements post fortnightly, so a
                        very recent range is often empty — widen the dates above.</>
                    : 'No products match this filter.'}
                </div>
              ) : (
                <div className={local.tableScroll}>
                  <table className={local.table}>
                    <thead>
                      <tr>
                        <th style={{ width: 28 }} />
                        <th>SKU</th>
                        <th>Channel</th>
                        <th className={local.numeric}>Units</th>
                        <th className={local.numeric}>Refunds</th>
                        <th className={local.numeric}>Net sales</th>
                        <th className={local.numeric}>Amazon fees</th>
                        <th className={local.numeric}>COGS</th>
                        <th className={local.numeric}>Net profit</th>
                        <th className={local.numeric}>Margin</th>
                        <th className={local.numeric}>ROI</th>
                        <th className={local.numeric}>Avg price</th>
                        <th className={local.numeric}>Breakeven</th>
                        <th>Findings</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <React.Fragment key={row.sku}>
                          <tr>
                            <td>
                              <button className={local.expandBtn} onClick={() => toggleRow(row.sku)}
                                aria-label={`Price bands for ${row.sku}`}>
                                {expanded === row.sku ? <FiChevronDown /> : <FiChevronRight />}
                              </button>
                            </td>
                            <td>{row.sku}</td>
                            <td>{row.channel
                              ? <span className={`${local.badge} ${local.badgeNeutral}`}>
                                  {row.channel === 'AFN' ? 'FBA'
                                    : row.channel === 'MFN' ? 'Seller' : 'Mixed'}
                                </span>
                              : <Blank />}</td>
                            <td className={local.numeric}>{count(row.units)}</td>
                            <td className={local.numeric}>
                              {row.refunded_units > 0
                                ? <>{count(row.refunded_units)}<span className={local.muted}>
                                    {' '}(<Money value={row.refunds} signed />)</span></>
                                : <Blank />}
                            </td>
                            <td className={local.numeric}><Money value={row.net_revenue} signed /></td>
                            <td className={local.numeric}><Money value={row.total_fees} /></td>
                            <td className={local.numeric}>
                              {row.has_cogs ? <Money value={row.cogs} /> : <Blank />}
                              <EstBadge show={row.cost_basis === 'estimated'} />
                            </td>
                            <td className={local.numeric}>
                              {row.has_cogs ? <Money value={row.net_profit} signed /> : <Blank />}
                              <EstBadge show={row.cost_basis === 'estimated'} />
                            </td>
                            <td className={local.numeric}>
                              {row.has_cogs ? <Percent value={row.margin_pct} signed /> : <Blank />}
                            </td>
                            <td className={local.numeric}>
                              <Percent value={row.roi_pct} signed />
                            </td>
                            <td className={local.numeric}>
                              <Money value={row.avg_realised_price} paise />
                              {row.distinct_prices > 1 && (
                                <span className={local.muted}> ·{row.distinct_prices}</span>
                              )}
                            </td>
                            <td className={local.numeric}>
                              {row.breakeven_price === null ? <Blank /> : (
                                <span className={row.below_breakeven ? local.loss : undefined}>
                                  <Money value={row.breakeven_price} paise />
                                </span>
                              )}
                            </td>
                            <td><FindingBadges findings={row.findings} /></td>
                          </tr>
                          {expanded === row.sku && (
                            <tr className={local.expandRow}>
                              <td colSpan={14}>
                                <div className={local.expandInner}>
                                  <div className={local.cardTitle} style={{ marginBottom: 8 }}>
                                    Units by the price they actually sold at
                                  </div>
                                  {!bands[row.sku] ? <span className={local.muted}>Loading…</span>
                                    : bands[row.sku].length === 0
                                      ? <span className={local.muted}>No transactions in this period.</span>
                                      : (
                                        <table className={local.subTable}>
                                          <thead>
                                            <tr>
                                              <th>Price</th>
                                              <th className={local.numeric}>Units</th>
                                              <th className={local.numeric}>Share</th>
                                              <th className={local.numeric}>Fee/unit</th>
                                              <th className={local.numeric}>Cost/unit</th>
                                              <th className={local.numeric}>Profit/unit</th>
                                              <th className={local.numeric}>Total profit</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {bands[row.sku].map((band) => (
                                              <tr key={band.price}>
                                                <td className={local.numeric}><Money value={band.price} paise /></td>
                                                <td className={local.numeric}>{count(band.units)}</td>
                                                <td className={local.numeric}><Percent value={band.share_of_units_pct} /></td>
                                                <td className={local.numeric}><Money value={band.fee_per_unit} paise /></td>
                                                <td className={local.numeric}><Money value={band.cogs_per_unit} paise /></td>
                                                <td className={local.numeric}><Money value={band.profit_per_unit} paise signed /></td>
                                                <td className={local.numeric}><Money value={band.total_profit} signed /></td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
          ) : (
            <>
              <p className={local.note} style={{ marginTop: 0, marginBottom: 10 }}>
                One row per order line. Every figure above sums from these.
              </p>
              {ordersError && (
                <div className={`${local.banner} ${local.bannerError}`}>
                  <FiAlertTriangle className={local.bannerIcon} />
                  <div>{ordersError.message}</div>
                </div>
              )}
              {ordersLoading && <div className={local.empty}>Loading order lines…</div>}
              {!ordersLoading && !ordersError && orderItems?.total === 0 && (
                <div className={local.empty}>
                  No order lines settled in this period. Settlements post
                  fortnightly, so a very recent range is often empty — widen the
                  dates and try again.
                </div>
              )}
              <div className={local.tableScroll}>
                <table className={local.table}>
                  <thead>
                    <tr>
                      <th>Date</th><th>Order</th><th>SKU</th>
                      <th className={local.numeric}>Units</th>
                      <th className={local.numeric}>Realised price</th>
                      <th className={local.numeric}>Principal</th>
                      <th className={local.numeric}>Fees</th>
                      <th className={local.numeric}>COGS</th>
                      <th className={local.numeric}>Net profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(orderItems?.rows ?? []).map((row) => (
                      <tr key={`${row.order_id}-${row.sku}`}>
                        <td>{formatDate(row.posted_date)}</td>
                        <td>{row.order_id}</td>
                        <td>{row.sku}</td>
                        <td className={local.numeric}>{count(row.units)}</td>
                        <td className={local.numeric}><Money value={row.realised_price} paise /></td>
                        <td className={local.numeric}><Money value={row.gross_principal} /></td>
                        <td className={local.numeric}><Money value={row.fees} /></td>
                        <td className={local.numeric}>
                          <Money value={row.cogs} /> <EstBadge show={row.cost_basis === 'estimated'} />
                        </td>
                        <td className={local.numeric}><Money value={row.net_profit} signed /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className={local.pagination}>
                <button className={local.ghost} disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}>Previous</button>
                <span>
                  Page {orderItems?.page ?? page} of{' '}
                  {Math.max(Math.ceil((orderItems?.total ?? 0) / (orderItems?.per_page || 50)), 1)}
                  {' · '}{count(orderItems?.total)} lines
                </span>
                <button className={local.ghost}
                  disabled={(orderItems?.page ?? page) * (orderItems?.per_page || 50) >= (orderItems?.total ?? 0)}
                  onClick={() => setPage((p) => p + 1)}>Next</button>
              </div>
            </>
          )}
        </div>

        {dashboard && <ProfitChange change={dashboard.change} />}
        {dashboard && (
          <ProfitTrend token={token} applied={applied} resolvedWindow={dashboard.window} />
        )}
      </div>
    </div>
  );
};

export default ProfitDashboard;
